import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import apiClient from "@/app/utils/apiClient";
import {
  LoginDto,
  RegisterDto,
  ResetPasswordDto,
  SendEmailDto,
  UpdateUserDto,
  User,
  UsersState,
} from "@/app/types";
import { deleteCookie } from "@/app/utils/auth";

// ---------------------- Thunks ----------------------

// REGISTER - Returns userId only
export const registerUser = createAsyncThunk<
  { message: string; userId: string }, // ✅ Updated return type
  RegisterDto,
  { rejectValue: string }
>("auth/registerUser", async (dto, { rejectWithValue }) => {
  try {
    const { confirmPassword, ...rest } = dto;
    const response = await apiClient.post("/auth/register", rest);
    return response.data; // { message, userId }
  } catch (err: any) {
    return rejectWithValue(
      err.response?.data?.message || "Registration failed",
    );
  }
});

// VERIFY REGISTER OTP - Activates account and returns user + token
export const verifyRegisterOtp = createAsyncThunk<
  { message: string; user: User },
  { userId: string; otp: string },
  { rejectValue: string }
>("auth/verifyRegisterOtp", async ({ userId, otp }, { rejectWithValue }) => {
  try {
    const response = await apiClient.post(
      "/auth/verify-register-otp",
      { userId, otp },
      { withCredentials: true },
    );
    return response.data; // { message, user }
  } catch (err: any) {
    return rejectWithValue(
      err.response?.data?.message || "OTP verification failed",
    );
  }
});

// LOGIN - Returns either { otpRequired: true, userId } OR { otpRequired: false, user, token }
export const loginUser = createAsyncThunk<
  | { otpRequired: true; userId: string; message: string }
  | { otpRequired: false; user: User; message: string },
  LoginDto,
  { rejectValue: string }
>("auth/loginUser", async (dto, { rejectWithValue }) => {
  try {
    // const response = await apiClient.post("/auth/login", dto, {
    //   withCredentials: true,
    // });
    const response = await apiClient.post(
      "/auth/login",
      {
        ...dto,
        panel: "customer", // 👈 Add this
      },
      {
        withCredentials: true,
      },
    );
    return response.data;
  } catch (err: any) {
    return rejectWithValue(err.response?.data?.message || "Login failed");
  }
});

export const resendEmailVerification = createAsyncThunk<
  { otpRequired: false; userId: string; message: string },
  SendEmailDto,
  { rejectValue: string }
>("auth/resend-email-verification", async (dto, { rejectWithValue }) => {
  try {
    const response = await apiClient.post(
      "/auth/resend-email-verification",
      dto,
      {
        withCredentials: true,
      },
    );
    return response.data;
  } catch (err: any) {
    return rejectWithValue(err.response?.data?.message || "Login failed");
  }
});

// VERIFY LOGIN OTP - Returns user + token
export const verifyLoginOtp = createAsyncThunk<
  { message: string; user: User },
  { userId: string; otp: string },
  { rejectValue: string }
>("auth/verifyLoginOtp", async ({ userId, otp }, { rejectWithValue }) => {
  try {
    const response = await apiClient.post(
      "/auth/verify-login-otp",
      { userId, otp },
      { withCredentials: true },
    );
    return response.data; // { message, user }
  } catch (err: any) {
    return rejectWithValue(
      err.response?.data?.message || "OTP verification failed",
    );
  }
});

// Get user profile
export const getProfile = createAsyncThunk<User, void, { rejectValue: string }>(
  "users/getProfile",
  async (_, { rejectWithValue }) => {
    try {
      const response = await apiClient.get("/users/profile", {
        withCredentials: true,
      });
      return response.data;
    } catch (err: any) {
      if (err.response?.status === 401) return rejectWithValue("SILENT");
      return rejectWithValue(
        err.response?.data?.message || "Failed to fetch profile",
      );
    }
  },
);

// Update user profile
export const updateProfile = createAsyncThunk<
  User,
  UpdateUserDto,
  { rejectValue: string }
>("users/updateProfile", async (dto, { rejectWithValue }) => {
  try {
    const response = await apiClient.patch("/users/profile", dto);
    return response.data;
  } catch (err: any) {
    return rejectWithValue(
      err.response?.data?.message || "Failed to update profile",
    );
  }
});

// Reset password
export const resetPassword = createAsyncThunk<
  { message: string },
  ResetPasswordDto,
  { rejectValue: string }
>("users/resetPassword", async (dto, { rejectWithValue }) => {
  try {
    const response = await apiClient.patch("/users/reset-password", dto);
    return response.data;
  } catch (err: any) {
    return rejectWithValue(
      err.response?.data?.message || "Failed to reset password",
    );
  }
});

// Logout
export const logoutUser = createAsyncThunk(
  "users/logout",
  async (_, { rejectWithValue }) => {
    try {
      const response = await apiClient.post("/auth/logout?panel=customer");
      deleteCookie("mindvaulet_customer");
      return response.data;
    } catch (err: any) {
      deleteCookie("mindvaulet_customer");
      return rejectWithValue(err.response?.data?.message || "Logout failed");
    }
  },
);

// Check if user is authenticated
export const checkAuthUser = createAsyncThunk<
  User,
  void,
  { rejectValue: string }
>("auth/checkAuthUser", async (_, { rejectWithValue }) => {
  try {
    const response = await apiClient.get(
      "/auth/is-authenticated",
      {
        withCredentials: true,
      },
    );
    return response.data.user;
  } catch (err: any) {
    if (err.response?.status === 401) return rejectWithValue("SILENT");
    return rejectWithValue(
      err.response?.data?.message || "Failed to verify authentication",
    );
  }
});

// ==================== Initial State ====================

export const initialState: UsersState = {
  loading: false,
  isProfileUpdating: false,
  isPasswordUpdating: false,
  error: null,
  loginError: null,
  registerError: null,
  otpError: null,
  message: null,
  users: [],
  currentUser: null,
  tempUserId: null,
  otpType: null,
};

// ---------------------- Slice ----------------------

const usersSlice = createSlice({
  name: "users",
  initialState,
  reducers: {
    clearUsersState: (state) => {
      state.currentUser = null;
      state.tempUserId = null;
      state.loading = false;
      state.error = null;
      state.message = null;
    },
    clearUsersError: (state) => {
      state.error = null;
      state.loginError = null;
      state.registerError = null;
    },
    clearUsersMessage: (state) => {
      state.message = null;
    },
    setTempUserId: (state, action: PayloadAction<string>) => {
      state.tempUserId = action.payload;
    },
    setOtpContext: (
      state,
      action: PayloadAction<{ userId: string; type: "REGISTER" | "LOGIN" }>,
    ) => {
      state.tempUserId = action.payload.userId;
      state.otpType = action.payload.type;

      sessionStorage.setItem("otpUserId", action.payload.userId);
      sessionStorage.setItem("otpType", action.payload.type);
    },
    clearOtpContext: (state) => {
      state.tempUserId = null;
      state.otpType = null;

      sessionStorage.removeItem("otpUserId");
      sessionStorage.removeItem("otpType");
    },
  },
  extraReducers: (builder) => {
    builder
      // ---- Register ----
      .addCase(registerUser.pending, (state) => {
        state.loading = true;
        state.registerError = null;
        state.message = null;
      })
      .addCase(registerUser.fulfilled, (state, action) => {
        state.loading = false;
        state.message = action.payload.message;

        state.tempUserId = action.payload.userId;
        state.otpType = "REGISTER";

        sessionStorage.setItem("otpUserId", action.payload.userId);
        sessionStorage.setItem("otpType", "REGISTER");
      })
      .addCase(registerUser.rejected, (state, action) => {
        state.loading = false;
        state.registerError = action.payload as string;
      })

      // ---- Verify Register OTP ----
      .addCase(verifyRegisterOtp.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.message = null;
      })
      .addCase(verifyRegisterOtp.fulfilled, (state, action) => {
        state.loading = false;
        state.currentUser = action.payload.user; // User is now logged in
        state.tempUserId = null; // Clear temp userId
        state.message = action.payload.message;
      })
      .addCase(verifyRegisterOtp.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })

      // ---- Login ----
      .addCase(loginUser.pending, (state) => {
        state.loading = true;
        state.loginError = null;
        state.message = null;
      })
      .addCase(loginUser.fulfilled, (state, action) => {
        state.loading = false;

        // if (action.payload.otpRequired) {
        //   // OTP required - save userId and wait for verification
        //   state.tempUserId = action.payload.userId;
        //   state.message = action.payload.message;
        //   state.currentUser = null;
        // } else {
        //   // Direct login (OTP disabled) - user is logged in
        //   state.currentUser = action.payload.user;
        //   state.tempUserId = null;
        //   state.message = action.payload.message;
        // }
        if (action.payload.otpRequired) {
          state.tempUserId = action.payload.userId;
          state.otpType = "LOGIN";

          sessionStorage.setItem("otpUserId", action.payload.userId);
          sessionStorage.setItem("otpType", "LOGIN");
        }
      })
      .addCase(loginUser.rejected, (state, action) => {
        state.loading = false;
        state.loginError = action.payload as string;
      })

      // ---- Verify Login OTP ----
      .addCase(verifyLoginOtp.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.message = null;
      })
      .addCase(verifyLoginOtp.fulfilled, (state, action) => {
        state.loading = false;
        state.currentUser = action.payload.user; // User is now logged in
        state.tempUserId = null; // Clear temp userId
        state.message = action.payload.message;
      })
      .addCase(verifyLoginOtp.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })

      // ---- Get Profile ----
      .addCase(getProfile.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getProfile.fulfilled, (state, action) => {
        state.currentUser = action.payload;
        state.loading = false;
      })
      .addCase(getProfile.rejected, (state, action) => {
        state.currentUser = null;
        state.loading = false;
        state.error = action.payload as string;
      })

      // ---- Update Profile ----
      .addCase(updateProfile.pending, (state) => {
        state.isProfileUpdating = true;
        state.error = null;
        state.message = null;
      })
      .addCase(updateProfile.fulfilled, (state, action) => {
        state.isProfileUpdating = false;
        state.currentUser = action.payload;
        state.message = "Profile updated successfully";
      })
      .addCase(updateProfile.rejected, (state, action) => {
        state.isProfileUpdating = false;
        state.error = action.payload as string;
      })

      // ---- Reset Password ----
      .addCase(resetPassword.pending, (state) => {
        state.isPasswordUpdating = true;
        state.error = null;
        state.message = null;
      })
      .addCase(resetPassword.fulfilled, (state, action) => {
        state.isPasswordUpdating = false;
        state.message = action.payload.message || "Password reset successfully";
      })
      .addCase(resetPassword.rejected, (state, action) => {
        state.isPasswordUpdating = false;
        state.error = action.payload as string;
      })

      // ---- Logout ----
      .addCase(logoutUser.pending, (state) => {
        state.currentUser = null;
      })
      .addCase(logoutUser.fulfilled, (state) => {
        state.currentUser = null;
        state.message = "";
        state.error = null;
      })
      .addCase(logoutUser.rejected, (state) => {
        state.currentUser = null;
      })

      // ---- Check Auth ----
      .addCase(checkAuthUser.pending, (state) => {
        state.loading = true;
      })
      .addCase(
        checkAuthUser.fulfilled,
        (state, action: PayloadAction<User>) => {
          state.loading = false;
          state.currentUser = action.payload;
        },
      )
      .addCase(checkAuthUser.rejected, (state, action) => {
        state.loading = false;
        state.currentUser = null;
        if (action.payload !== "SILENT") {
          state.error = action.payload as string;
        }
      })
      // ---- resendEmailVerification ----
      .addCase(resendEmailVerification.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.message = null;
      })
      .addCase(resendEmailVerification.fulfilled, (state, action) => {
        state.loading = false;
        state.message = action.payload.message;

        // safety: userId dubara store kar lo
        state.tempUserId = action.payload.userId;
      })
      .addCase(resendEmailVerification.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  },
});

export const {
  clearUsersState,
  clearUsersError,
  clearUsersMessage,
  setTempUserId,
  clearOtpContext,
} = usersSlice.actions;

export default usersSlice.reducer;
